import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";

/**
 * ProtectedRoute — admin gate.
 *
 * Three-state pattern per the Emergent Auth playbook:
 *   user undefined/loading  → render loading UI
 *   user from location.state (just authenticated) → render children immediately
 *   user resolved from AuthProvider → render children if admin, else redirect
 *
 * The `location.state.user` fast-path avoids the race where AuthProvider
 * state hasn't propagated yet on the very first render after navigate().
 */
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const stateUser = location.state?.user;

  // Fast path: user just authenticated via /auth/callback
  if (stateUser?.is_admin) {
    return children;
  }

  if (loading) {
    return (
      <div
        className="min-h-screen grid place-items-center bg-[#060a1a] text-slate-400"
        data-testid="auth-loading"
      >
        <div className="flex items-center gap-3 text-sm font-mono-display">
          <span className="w-2 h-2 rounded-full bg-blue-400 animate-pulse" />
          checking session…
        </div>
      </div>
    );
  }

  if (!user || !user.is_admin) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

export default ProtectedRoute;
